const GAP = 1000;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('queue', 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const store = db.createObjectStore('queue', { keyPath: 'id', autoIncrement: true });
      store.createIndex('by_order', 'order');
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

function transaction(mode) {
  return openDb().then((db) => db.transaction('queue', mode).objectStore('queue'));
}

async function rebalance(items) {
  const store = await transaction('readwrite');
  const rebalanced = items.map((item, i) => ({ ...item, order: i * GAP }));
  rebalanced.forEach((item) => store.put(item));
  return rebalanced;
}

export async function getQueue() {
  const store = await transaction('readonly');
  const index = store.index('by_order');
  return new Promise((resolve, reject) => {
    const items = [];
    const request = index.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        items.push(cursor.value);
        cursor.continue();
      } else {
        resolve(items);
      }
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

async function lastOrder() {
  const store = await transaction('readonly');
  const index = store.index('by_order');
  return new Promise((resolve, reject) => {
    const request = index.openCursor(null, 'prev');

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      resolve(cursor ? cursor.value.order : null);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function addToQueue({ itemId, itemType, audioUrl, title, showName, artworkUrl, feedItemUrl }) {
  const previousLastOrder = await lastOrder();
  const newOrder = previousLastOrder === null ? GAP : previousLastOrder + GAP;

  const store = await transaction('readwrite');
  store.put({ itemId, itemType, audioUrl, title, showName, artworkUrl, feedItemUrl, order: newOrder });

  return getQueue();
}

export async function removeFromQueueAt(index) {
  const existing = await getQueue();
  const target = existing[index];
  if (!target) return existing;

  const store = await transaction('readwrite');
  store.delete(target.id);

  return existing.filter((_, i) => i !== index);
}

export async function reorderQueue(fromIndex, toIndex) {
  const existing = await getQueue();
  const items = [...existing];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);

  const prevItem = items[toIndex - 1];
  const nextItem = items[toIndex + 1];

  let newOrder;
  let needsRebalance = false;

  if (!prevItem && !nextItem) {
    newOrder = GAP;
  } else if (!prevItem) {
    newOrder = nextItem.order - GAP;
  } else if (!nextItem) {
    newOrder = prevItem.order + GAP;
  } else {
    newOrder = (prevItem.order + nextItem.order) / 2;
    if (
      newOrder === prevItem.order ||
      newOrder === nextItem.order ||
      nextItem.order - prevItem.order < 1
    ) {
      needsRebalance = true;
    }
  }

  if (needsRebalance) {
    return rebalance(items);
  }

  const movedWithOrder = { ...moved, order: newOrder };
  items[toIndex] = movedWithOrder;

  const store = await transaction('readwrite');
  store.put(movedWithOrder);

  return items;
}

export async function clearQueue() {
  const store = await transaction('readwrite');
  store.clear();
}
